import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Loading from '../Loading';
import { NavBar } from '../../modules/app';
import { AppLayout, ContentWrapper } from './layout.style';
import { Label } from '../uielements';
import { NewReleasedUpdate } from '../../modules/Pages/newReleaseUpdate';
import { useAuth } from '../../hooks/AuthContext';
import { getChatBotAgentId } from '../../helper/envHelper';

const RootLayout = () => {
  const [loader, setLoader] = useState(false);
  const { session } = useAuth();
  const location = useLocation();

  const hideNavbar = location.pathname.includes('/auth/invitations/');

  /**
   * Get ChatBot SDK URL based on environment
   */
  const getChatBotSDKUrl = (env) => {
    switch (env) {
      case 'production':
      case 'au_production':
        return process.env.REACT_APP_PROD_SDK_URL;

      case 'stage':
      case 'au_stage':
      default:
        return process.env.REACT_APP_STAGE_SDK_URL;
    }
  };

  /**
   * Load and initialize ChatBot widget for logged-in users
   */
  useEffect(() => {
    if (session) {
      // Only initialize if not already loaded
      if (!window.ChatBotEmbedLoaded) {
        const script = document.createElement('script');
        script.src = getChatBotSDKUrl(process.env.REACT_APP_ENV);
        script.async = true;
        script.id = 'chatbot-embed-script';

        script.onload = () => {
          if (window.ChatBotEmbed) {
            window.ChatBotEmbed({
              botId: getChatBotAgentId(process.env.REACT_APP_ENV),
              debug: true,
              position: 'bottom-right',
              startMinimized: true,
              autoOpen: true,
              theme: {
                primaryColor: '#E6B471',
                backgroundColor: '#ffffff',
                textColor: '#111827',
                borderRadius: '12px',
                fontSize: '14px',
              },
            });
            window.ChatBotEmbedLoaded = true;
          }
        };

        document.body.appendChild(script);
      }
    }
  }, [session]);

  /**
   * Cleanup ChatBot widget on logout
   */
  useEffect(() => {
    if (!session) {
      // Aggressive cleanup
      try {
        // 1. Call SDK destroy if available
        if (window.ChatBotWidget?.destroy) {
          window.ChatBotWidget.destroy();
        }

        // 2. Remove ALL shadow DOM hosts (by various selectors)
        const shadowHosts = document.querySelectorAll(
          '[id^="chatbot-widget-"], [class*="chatbot"], [data-bot-id]'
        );
        shadowHosts.forEach((host) => {
          try {
            host.remove();
          } catch (e) {
            console.error('Error removing host:', e);
          }
        });

        // 3. Remove any script tags
        const scripts = Array.from(document.querySelectorAll('script'));
        scripts.forEach((script) => {
          if (script.src && script.src.includes('chatbot')) {
            try {
              script.remove();
            } catch (e) {
              console.error('Error removing script:', e);
            }
          }
        });

        // 4. Reset all window references
        window.ChatBotEmbedLoaded = false;
        window.ChatBotEmbed = undefined;
        window.ChatBotWidget = undefined;

        if (process.env.NODE_ENV === 'development') {
          console.log('[ChatBot Widget] Complete cleanup on logout');
        }
      } catch (error) {
        console.error('[ChatBot Widget] Cleanup error:', error);
      }
    }
  }, [session]);

  return (
    <>
      <AppLayout>
        {!hideNavbar && <NavBar />}
        <ContentWrapper hideNavbar={hideNavbar}>
          <Outlet context={[loader, setLoader]} />
          <Loading visible={loader} />
        </ContentWrapper>
        {!hideNavbar && <NewReleasedUpdate />}
        {!hideNavbar && (
          <Label
            style={{
              position: 'fixed',
              bottom: '6px',
              right: '20px',
              zIndex: '-10',
            }}
          >
            Version:{session?.webVersionNumber || process.env.REACT_APP_VERSION}
          </Label>
        )}
      </AppLayout>
    </>
  );
};

export default RootLayout;
